import React from "react";
import { Box, Card, Grid, Skeleton, Stack, Typography } from "@mui/material";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import ClassOutlinedIcon from "@mui/icons-material/ClassOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";

type Summary = Record<string, number>;

const CARDS = [
  { key: "students_with_classes", label: "Students With Classes",
    sub: "Have at least one class assignment",
    color: "#1b3a6b", bg: "#eff6ff", Icon: PeopleAltOutlinedIcon },
  { key: "students_without_classes", label: "Without Classes",
    sub: "No class assignments yet",
    color: "#92400e", bg: "#fef3c7", Icon: PersonOffOutlinedIcon },
  { key: "total_active_assignments", label: "Active Assignments",
    sub: "Current student-to-class assignments",
    color: "#166534", bg: "#dcfce7", Icon: AssignmentTurnedInOutlinedIcon },
  { key: "total_classes", label: "Total Classes",
    sub: "Distinct classes in scope",
    color: "#6b21a8", bg: "#f3e8ff", Icon: ClassOutlinedIcon },
  { key: "total_staff", label: "Total Staff",
    sub: "Distinct instructors teaching",
    color: "#3730a3", bg: "#e0e7ff", Icon: BadgeOutlinedIcon },
  { key: "online_assignments", label: "Online",
    sub: "Assignments delivered online",
    color: "#0e7490", bg: "#cffafe", Icon: VideocamOutlinedIcon },
  { key: "in_person_assignments", label: "In Person",
    sub: "Assignments delivered on-site",
    color: "#0f766e", bg: "#ccfbf1", Icon: StorefrontOutlinedIcon,
    fallbackKey: "offline_assignments" },
] as const;

export default function SummaryCards({ summary, loading }: { summary: Summary; loading: boolean }) {
  return (
    <Grid container spacing={1.5} mb={2}>
      {CARDS.map((c) => {
        const val = summary[c.key] ?? summary[(c as any).fallbackKey ?? ""] ?? 0;
        const { Icon } = c;
        return (
          <Grid key={c.key} item xs={12} sm={6} md={4} lg={3} xl={2}>
            <Card variant="outlined" sx={{ p: 1.5, borderRadius: 2, height: "100%" }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box sx={{
                  width: 44, height: 44, borderRadius: "12px",
                  background: c.bg, color: c.color, display: "flex",
                  alignItems: "center", justifyContent: "center",
                }}>
                  <Icon />
                </Box>
                <Box flex={1} minWidth={0}>
                  <Typography variant="caption" color="text.secondary">{c.label}</Typography>
                  {loading ? <Skeleton width="60%" height={28} /> : (
                    <Typography variant="h5" fontWeight={700} color={c.color} lineHeight={1.15}>
                      {Number(val).toLocaleString()}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                    {c.sub}
                  </Typography>
                </Box>
              </Stack>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
}
